<template>
  <header class="fixed md:sticky top-0 z-50 w-full">
    <GlobalBanner />
    <div class="bg-warning py-3" v-if="hasAlert()">
      <BaseContainer>
        <div
          class="flex flex-col md:flex-row justify-center items-center gap-1 md:gap-4 font-semibold text-headings text-sm"
        >
          {{ checkoutStatus.text }}
          <a
            v-if="checkoutStatus.link"
            :href="checkoutStatus.link"
            target="_blank"
            class="underline"
          >
            {{ checkoutStatus.linkText }}
          </a>
        </div>
      </BaseContainer>
    </div>

    <div class="bg-surface-light border-b border-surface-grey md:py-4">
      <BaseContainer>
        <div
          class="flex flex-col md:flex-row justify-between items-center w-full max-w-100 mx-auto md:max-w-full"
        >
          <RouterLink
            to="/"
            v-if="!isEmbed()"
            :class="[
              'transition-all flex-shrink-0 origin-center ease-linear flex items-center md:p-0 md:h-12 md:mb-0 md:w-50',
              showLogo && route.name !== 'checkout-seats' ? 'w-35 h-16 pt-4' : 'py-0 w-0 h-0',
            ]"
          >
          <GlobalCustomerLogo />
          </RouterLink>

          <div
            :class="[
              'grid grid-cols-4 mx-auto md:flex items-center transition-all overflow-hidden justify-center md:h-auto md:p-0 md:flex-1 w-full md:justify-end',
              scrollingDown && lastScrollY > 100 ? 'h-0 py-0' : 'h-18 py-4',
            ]"
          >
            <div
              class="flex items-center justify-center"
              v-for="(step, index) in steps"
              :key="index"
            >
              <div
                class="flex flex-col md:flex-row items-center gap-1 md:gap-2"
              >
                <div
                  :class="[
                    'w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold',
                    currentStep == 4 && index == 3
                      ? 'bg-success text-white'
                      : currentStep == index + 1
                        ? 'bg-primary text-primary-text'
                        : 'bg-surface-grey text-text opacity-50',
                  ]"
                >
                  <CheckmarkIcon
                    v-if="
                      currentStep > index + 1 ||
                      (currentStep == 4 && index == 3)
                    "
                    class="w-3 h-3 fill-current"
                  />
                  <span v-else>
                    {{ index + 1 }}
                  </span>
                </div>

                <span
                  :class="[
                    'text-xs md:text-sm font-semibold',
                    currentStep == 4 && index == 3
                      ? 'text-success'
                      : currentStep == index + 1
                        ? 'text-primary'
                        : 'text-text opacity-50',
                  ]"
                >
                  {{ step }}
                </span>
              </div>

              <div
                v-if="index < steps.length - 1"
                class="w-10 h-px bg-surface-grey ml-4 mr-4 hidden md:block"
              />
            </div>
          </div>
        </div>
      </BaseContainer>

      <div v-if="route.name !== 'checkout-seats' && activeExpire > 0"
        class="w-full flex md:hidden grow items-center gap-1 font-semibold bg-surface-grey-light text-sm border-t border-surface-grey px-4 py-2"
      >
        <StopwatchIcon class="fill-primary w-6 h-6" />
        <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
      </div>

    </div>
  </header>
  <div :class="['md:h-0', calculateHeaderHeight()]"></div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
  import { useCartStore } from '@/stores/cart';
  import { isEmbed } from '@/utils/isEmbed';

  import GlobalBanner from '../globals/GlobalBanner.vue';
  import GlobalCustomerLogo from '../globals/GlobalCustomerLogo.vue';
  import CheckmarkIcon from '../icons/CheckmarkIcon.vue';
  import BaseContainer from '../ui/BaseContainer.vue';
  import BaseCountdown from '../ui/BaseCountdown.vue';

  defineProps<{
    currentStep: number;
  }>();

  const cart = useCartStore();
  const route = useRoute();
  const { t } = useI18n();

  const checkoutStatus = {
    text: '',
    link: 'https://safeticket.dk/status',
    linkText: 'Se status',
  };
  
  const activeExpire = computed(() =>
    route.name === 'checkout-payment' ? cart.paymentFailedExpire : cart.cartExpire
  );

  function hasAlert() {
    return route.name !== 'checkout-seats' && checkoutStatus?.text;
  }

  function calculateHeaderHeight() {
    if (route.name === 'checkout-seats') {
      return 'h-20';
    }
    if (isEmbed()) {
      if (hasAlert()) {
        return 'h-36';
      }
      return 'h-24';
    } 
    else {
      if (hasAlert()) {
        return 'h-58';
      }
      return 'h-35';
    }
  }

  const countdownDate = new Date(Date.now() + 10 * 60 * 1000).getTime() / 1000;
  const steps = computed(() => [
    t('checkout.tickets'),
    t('checkout.information'),
    t('checkout.payment'),
    t('checkout.receipt'),
  ]);

  const showLogo = ref(true);
  const scrollingDown = ref(false);
  const lastScrollY = ref(0);

  function handleScroll() {
    showLogo.value = window.scrollY < 50;
    scrollingDown.value = window.scrollY > lastScrollY.value;

    lastScrollY.value = window.scrollY;
  }

  onMounted(() => {
    window.addEventListener('scroll', handleScroll);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
  });
</script>
